import { Platform } from 'react-native';

// Configuration
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1920;
const QUALITY = 0.85; // 85% quality (good balance)

/**
 * Resize an image if it's too large
 * @param imageUri - URI of the image
 * @param originalWidth - Original width (optional, for optimization)
 * @param originalHeight - Original height (optional, for optimization)
 * @returns URI of resized image or original if no resize needed
 */
export const resizeImageIfNeeded = async (
  imageUri: string,
  originalWidth?: number,
  originalHeight?: number
): Promise<string> => {
  // Only resize if image is larger than our max
  const needsResize = 
    !originalWidth || 
    !originalHeight || 
    originalWidth > MAX_WIDTH || 
    originalHeight > MAX_HEIGHT;

  if (!needsResize) {
    console.log('Image is small enough, skipping resize');
    return imageUri;
  }

  if (Platform.OS === 'web') {
    return await resizeImageWeb(imageUri);
  } else {
    return await resizeImageNative(imageUri, originalWidth, originalHeight);
  }
};

/**
 * Resize image on native platforms using expo-image-manipulator
 */
const resizeImageNative = async (
  imageUri: string,
  originalWidth?: number,
  originalHeight?: number
): Promise<string> => {
  const { manipulateAsync, SaveFormat } = require('expo-image-manipulator');
  
  console.log(`Resizing image from ${originalWidth}x${originalHeight}`);
  
  // Calculate new dimensions maintaining aspect ratio
  const dimensions = calculateDimensions(originalWidth, originalHeight);
  
  const manipResult = await manipulateAsync(
    imageUri,
    [{ resize: dimensions }],
    { 
      compress: QUALITY,
      format: SaveFormat.JPEG 
    }
  );

  console.log(`Image resized to ${dimensions.width}x${dimensions.height}`);
  return manipResult.uri;
};

/**
 * Resize image on web using browser-image-compression
 */
const resizeImageWeb = async (imageUri: string): Promise<string> => {
  const imageCompression = require('browser-image-compression');
  
  // Fetch the image as a file
  const response = await fetch(imageUri);
  const blob = await response.blob();
  const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
  
  const options = {
    maxSizeMB: 2, // Max file size
    maxWidthOrHeight: MAX_WIDTH,
    useWebWorker: true,
    initialQuality: QUALITY,
  };
  
  const compressedFile = await imageCompression(file, options);
  
  // Convert back to URI
  return URL.createObjectURL(compressedFile);
};

/**
 * Calculate new dimensions maintaining aspect ratio
 */
const calculateDimensions = (
  width: number | undefined,
  height: number | undefined
): { width: number; height: number } => {
  if (!width || !height) {
    return { width: MAX_WIDTH, height: MAX_HEIGHT };
  }

  const aspectRatio = width / height;

  if (width > height) {
    // Landscape
    if (width > MAX_WIDTH) {
      return {
        width: MAX_WIDTH,
        height: Math.round(MAX_WIDTH / aspectRatio),
      };
    }
  } else {
    // Portrait or square
    if (height > MAX_HEIGHT) {
      return {
        width: Math.round(MAX_HEIGHT * aspectRatio),
        height: MAX_HEIGHT,
      };
    }
  }

  // Image is already small enough
  return { width, height };
};

/**
 * Get image dimensions from URI
 */
export const getImageDimensions = async (
  imageUri: string
): Promise<{ width: number; height: number } | null> => {
  if (Platform.OS === 'web') {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve(null);
      };
      img.src = imageUri;
    });
  } else {
    // On native, dimensions are usually provided by ImagePicker
    // If not, you can use expo-image-manipulator to get info
    const { manipulateAsync, SaveFormat } = require('expo-image-manipulator');
    try {
      const imageInfo = await manipulateAsync(
        imageUri,
        [],
        { format: SaveFormat.JPEG }
      );
      return { width: imageInfo.width, height: imageInfo.height };
    } catch {
      return null;
    }
  }
};

/**
 * Check if a MIME type should be compressed with ZIP
 * Already-compressed formats (JPEG, PNG) don't benefit from ZIP
 */
export const shouldCompressWithZip = (mimeType: string): boolean => {
  const alreadyCompressed = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp',
    'video/mp4',
    'video/quicktime',
  ];
  
  return !alreadyCompressed.includes(mimeType.toLowerCase());
};

